import { lazy } from 'react'

const RELOAD_KEY = 'socialbox_chunk_reload_guard'

const DYNAMIC_IMPORT_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /ChunkLoadError/i,
  /Loading chunk [\d]+ failed/i,
]

function getCurrentIndexAssetId() {
  if (typeof document === 'undefined') return 'unknown-index'
  const script = document.querySelector('script[src*="/assets/index-"]')
  if (!script || !script.src) return 'unknown-index'
  try {
    const url = new URL(script.src, window.location.origin)
    return url.pathname
  } catch {
    return script.src
  }
}

function toErrorMessage(value) {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value?.message === 'string') return value.message
  return String(value)
}

export function isChunkLoadFailure(value) {
  const msg = toErrorMessage(value)
  if (!msg) return false
  return DYNAMIC_IMPORT_PATTERNS.some((pattern) => pattern.test(msg))
}

async function loadRouteModule(factory) {
  try {
    return await factory()
  } catch (err) {
    if (isChunkLoadFailure(err)) {
      triggerRecoveryReload(err)
      return new Promise(() => {})
    }
    throw err
  }
}

/** Lazy route import with one-shot full reload on stale chunk hashes after deploy. */
export function lazyRoute(factory) {
  return lazy(() => loadRouteModule(factory))
}

function shouldReloadOnceForCurrentBundle() {
  if (typeof window === 'undefined' || !window.sessionStorage) return false
  const currentBundle = getCurrentIndexAssetId()
  const attemptedBundle = window.sessionStorage.getItem(RELOAD_KEY)
  if (attemptedBundle === currentBundle) return false
  window.sessionStorage.setItem(RELOAD_KEY, currentBundle)
  return true
}

export function triggerRecoveryReload(reason) {
  if (!isChunkLoadFailure(reason)) return false
  if (!shouldReloadOnceForCurrentBundle()) return false
  window.location.reload()
  return true
}

export function installChunkLoadRecovery() {
  if (typeof window === 'undefined') return

  window.addEventListener('unhandledrejection', (event) => {
    triggerRecoveryReload(event?.reason)
  })

  window.addEventListener('error', (event) => {
    const target = event?.target
    if (target && target.tagName === 'SCRIPT') {
      const src = target.getAttribute('src') || ''
      if (src.includes('/assets/')) {
        triggerRecoveryReload('Importing a module script failed')
        return
      }
    }
    triggerRecoveryReload(event?.error || event?.message)
  }, true)
}
